import { inject, Injectable, signal, type Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, map, Subject, tap, type Observable } from 'rxjs';

import { ApiClientService } from '@ebt/shared-api';

export interface NotificationItem {
  id: string;
  recipientType: string;
  recipientId: string;
  eventKey: string;
  title: string;
  body: string;
  linkUrl: string | null;
  payload: unknown;
  readAt: string | null;
  tenantId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationsPage {
  items: NotificationItem[];
  total: number;
  unreadCount: number;
}

/**
 * Typed wrapper over `/api/v1/notifications/*`. Returns a flat
 * `NotificationsPage` (items + counts) rather than the raw envelope so
 * the notification centre component doesn't have to inspect `meta`.
 */
@Injectable({ providedIn: 'root' })
export class NotificationsService {
  private readonly api = inject(ApiClientService);

  constructor() {
    // The /notifications page is rarely mounted when a write happens (the user
    // is on leads/jobs/etc.), so its own mutation reaction can't fire. This
    // service is constructed at shell load (the bell injects it) and lives for
    // the app's lifetime, so react here too: refresh the shared unread count
    // the moment any write completes, updating the always-present bell badge
    // without waiting for its 60s poll. Debounced to coalesce the burst of
    // requests a single form submit can make.
    this._mutations
      .pipe(debounceTime(300), takeUntilDestroyed())
      .subscribe(() => this.refreshUnreadCount());
  }

  private readonly _unreadCount = signal(0);

  /**
   * Single source of truth for the unread badge.
   *
   * The bell and the /notifications page are separate components that both
   * mutate read state, so each keeping its own count let them drift — most
   * visibly, "Mark All Read" on the page left the bell showing a stale
   * number until a full reload. Every read/write goes through here instead.
   */
  readonly unreadCount: Signal<number> = this._unreadCount.asReadonly();

  /** Push a server-authoritative count (from a list response). */
  setUnreadCount(n: number): void {
    this._unreadCount.set(Math.max(0, n));
  }

  /** Optimistic local adjustment, e.g. after marking one item read. */
  adjustUnreadCount(delta: number): void {
    this._unreadCount.update((n) => Math.max(0, n + delta));
  }

  private readonly _mutations = new Subject<void>();

  /**
   * Fires after the user completes a write (POST/PUT/PATCH/DELETE) anywhere
   * in the app — an activity feed of the user's own actions is the most
   * likely source of new notifications, so the notifications view can poll
   * the delta cursor immediately instead of waiting for the next timer tick.
   * Published by `notificationRefreshInterceptor`.
   */
  readonly mutations$: Observable<void> = this._mutations.asObservable();

  /** Signal a completed user write. */
  notifyMutation(): void {
    this._mutations.next();
  }

  /**
   * Pull the authoritative unread count (pageSize 1 — we only need
   * `meta.unreadCount`, which `list()` publishes via `setUnreadCount`).
   * Errors are swallowed: a failed background refresh must not surface.
   */
  private refreshUnreadCount(): void {
    this.list(1, 1).subscribe({ error: () => undefined });
  }

  list(page = 1, pageSize = 10): Observable<NotificationsPage> {
    return this.api
      .getWithMeta<NotificationItem[]>('/notifications', {
        params: { page, pageSize },
      })
      .pipe(
        map((response) => ({
          items: response.data,
          total: (response.meta?.['pagination'] as { total?: number } | undefined)?.total ?? 0,
          unreadCount:
            typeof response.meta?.['unreadCount'] === 'number'
              ? (response.meta['unreadCount'] as number)
              : 0,
        })),
        tap((page) => this.setUnreadCount(page.unreadCount)),
      );
  }

  markAllRead(): Observable<unknown> {
    return this.api
      .post<unknown, Record<string, never>>(
        '/notifications/mark-all-read',
        {},
        {
          idempotent: false,
        },
      )
      .pipe(tap(() => this.setUnreadCount(0)));
  }

  markRead(id: string): Observable<unknown> {
    return this.api
      .patch<unknown, Record<string, never>>(`/notifications/${id}/mark-read`, {})
      .pipe(tap(() => this.adjustUnreadCount(-1)));
  }

  dismiss(id: string): Observable<void> {
    return this.api.delete<void>(`/notifications/${id}`);
  }
}
